import type { ExistingCustomerAddressHit } from '../models/opportunity.model';
import { coerceText } from './coerce-text';

/** Minimal shape for ranking addresses (party search hits and opportunity detail). */
export type CustomerAddressPickCandidate = {
  addressLine1: string;
  addressLine2?: string | null;
  pinCode?: string | null;
  city?: string | null;
  district?: string | null;
  stateCode?: string | null;
  countryCode?: string | null;
  addressTypeCode?: string | null;
};

const INDIAN_PIN_REGEX = /^[1-9]\d{5}$/;

export function isValidIndianPinCode(pin: unknown): boolean {
  return INDIAN_PIN_REGEX.test(coerceText(pin).trim());
}

function parseAddressRow(row: unknown): ExistingCustomerAddressHit | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const address = row as Record<string, unknown>;
  const addressLine1 = coerceText(address['addressLine1'] ?? '').trim();
  if (!addressLine1) {
    return null;
  }
  return {
    addressLine1,
    addressLine2: optionalText(address['addressLine2']),
    pinCode: optionalText(address['pinCode']),
    city: optionalText(address['city']),
    district: optionalText(address['district']),
    stateCode: optionalText(address['stateCode']),
    countryCode: optionalText(address['countryCode']),
    addressTypeCode: optionalText(address['addressTypeCode']),
  };
}

function optionalText(value: unknown): string | undefined {
  const trimmed = coerceText(value ?? '').trim();
  return trimmed || undefined;
}

/** Parse `addresses` array (or legacy single `address` line) from a party/search row. */
export function parseCustomerAddressesFromRecord(
  record: Record<string, unknown>,
): ExistingCustomerAddressHit[] {
  const addresses = record['addresses'];
  if (Array.isArray(addresses) && addresses.length) {
    return addresses
      .map((row) => parseAddressRow(row))
      .filter((item): item is ExistingCustomerAddressHit => item != null);
  }

  const line1 = coerceText(record['address'] ?? '').trim();
  if (!line1) {
    return [];
  }
  return [{ addressLine1: line1 }];
}

function addressQualityScore(address: CustomerAddressPickCandidate): number {
  let score = 0;
  const pin = coerceText(address.pinCode).trim();
  if (isValidIndianPinCode(pin)) {
    score += 100;
  } else if (pin === '000000' || pin === '0') {
    score -= 40;
  } else if (pin) {
    score -= 10;
  }

  if (address.addressLine1.trim()) {
    score += 5;
  }

  const state = coerceText(address.stateCode).trim();
  if (isValidIndianPinCode(state) && !isValidIndianPinCode(pin)) {
    score -= 25;
  }

  const city = coerceText(address.city).trim().toLowerCase();
  const district = coerceText(address.district).trim().toLowerCase();
  if (city && district && city === district && city.length > 3) {
    score -= 5;
  }

  return score;
}

function addressTypePreference(
  address: CustomerAddressPickCandidate,
  isCorporate: boolean,
): number {
  const type = coerceText(address.addressTypeCode).toLowerCase();
  if (isCorporate) {
    if (type.includes('registered')) return 8;
    if (type.includes('head')) return 5;
    return 0;
  }
  if (type.includes('permanent')) return 8;
  if (type.includes('current')) return 5;
  return 0;
}

/**
 * When a party has multiple addresses, prefer one with a valid Indian pincode and
 * sensible field mapping; tie-break by address type (permanent / registered office).
 */
export function pickBestCustomerAddress<T extends CustomerAddressPickCandidate>(
  addresses: readonly T[] | undefined,
  options?: { isCorporate?: boolean },
): T | undefined {
  if (!addresses?.length) {
    return undefined;
  }
  if (addresses.length === 1) {
    return addresses[0];
  }

  const isCorporate = options?.isCorporate === true;
  const ranked = [...addresses].sort((left, right) => {
    const byQuality = addressQualityScore(right) - addressQualityScore(left);
    if (byQuality !== 0) {
      return byQuality;
    }
    return (
      addressTypePreference(right, isCorporate) -
      addressTypePreference(left, isCorporate)
    );
  });

  return ranked[0];
}

export function pickBestCustomerAddressFromRecord(
  record: Record<string, unknown>,
  options?: { isCorporate?: boolean },
): ExistingCustomerAddressHit | undefined {
  return pickBestCustomerAddress(
    parseCustomerAddressesFromRecord(record),
    options,
  );
}

const PARTY_ADDRESS_PATH = /^addresses\.\d+\.(.+)$/i;

/** Resolve workflow `addresses.0.*` paths using the best address, not always index 0. */
export function resolvePartyRecordPath(
  record: Record<string, unknown>,
  path: string,
  options?: { isCorporate?: boolean },
): unknown {
  const trimmed = path.trim();
  if (!trimmed) {
    return undefined;
  }

  const addressFieldMatch = PARTY_ADDRESS_PATH.exec(trimmed);
  if (addressFieldMatch) {
    const fieldKey = addressFieldMatch[1];
    const best = pickBestCustomerAddressFromRecord(record, options);
    if (!best) {
      return undefined;
    }
    return best[fieldKey as keyof ExistingCustomerAddressHit];
  }

  if (!trimmed.includes('.')) {
    return record[trimmed];
  }

  let current: unknown = record;
  for (const part of trimmed.split('.').filter(Boolean)) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
